<template>
<div class="issue-home-page -mx-4 -mt-4">
  <page-header
    v-if="issue && issue.id"
    :title="issue.name"
    :subtitle="$t('issue_home_page_subtitle')"
    variant="issue"
  >
    <div class="issue-home-header-right">
      <div class="issue-home-header-meta">
        <span
          class="issue-home-header-meta__status"
          :class="issue.is_open ? 'is-open' : 'is-closed'"
        >
          <span class="issue-home-header-meta__status-dot" />
          {{ issue.is_open ? $t('opened') : $t('closed') }}
        </span>

        <span v-if="issue.estimated_resolution_date" class="issue-home-header-meta__divider" />

        <div v-if="issue.estimated_resolution_date" class="issue-home-header-meta__item">
          <span class="issue-home-header-meta__label">{{ $t('estimated_resolution_date') }}</span>
          <span class="issue-home-header-meta__value issue-home-header-meta__value--date">
            <b-icon pack="fas" icon="calendar-alt" size="is-small" />
            {{ issueEstimatedResolutionLabel }}
          </span>
        </div>

        <span class="issue-home-header-meta__divider" />

        <div class="issue-home-header-meta__item">
          <span class="issue-home-header-meta__label">{{ $t('issue_date') }}</span>
          <span class="issue-home-header-meta__value issue-home-header-meta__value--date">
            <b-icon pack="fas" icon="calendar-alt" size="is-small" />
            {{ issueDateLabel }}
          </span>
        </div>

        <template v-if="!issue.is_open && issue.closed_at">
          <span class="issue-home-header-meta__divider" />

          <div class="issue-home-header-meta__item">
            <span class="issue-home-header-meta__label">{{ $t('closed_date') }}</span>
            <span class="issue-home-header-meta__value issue-home-header-meta__value--date">
              <b-icon pack="fas" icon="calendar-check" size="is-small" />
              {{ issueClosedDateLabel }}
            </span>
          </div>
        </template>
      </div>

      <issue-home-header-actions
        :entity-id="entityId"
        :activity-id="activityId"
        :subactivity-id="subactivityId"
        :issue="issue"
        :issue-tasks="issueTasks"
        :status-color="statusColor"
      />
    </div>
  </page-header>

  <div class="px-4 top-bar">
    <!-- Resumen de la incidencia -->
    <div class="hidden w-full min-w-0 sm:block">
      <div class="issue-summary">
        <div class="issue-summary-bar">
          <div
            v-for="field in issueSummaryFields"
            :key="field.key"
            class="issue-summary-field"
          >
            <span class="issue-summary-field__label">{{ $t(field.labelKey) }}</span>
            <span class="issue-summary-field__value" :title="field.value">
              <b-icon pack="fas" :icon="field.icon" size="is-small" />
              <span>{{ field.value }}</span>
            </span>
          </div>
        </div>

        <button
          v-if="issueExtraItems.length"
          type="button"
          class="issue-details-toggle"
          @click="toggleIssueDetails"
        >
          <b-icon pack="fas" :icon="showIssueDetails ? 'chevron-up' : 'chevron-down'" size="is-small"></b-icon>
          {{ showIssueDetails ? $t('hide') : $t('details') }}
          <span class="issue-details-count">{{ issueExtraItems.length }}</span>
        </button>

        <div v-show="showIssueDetails && issueExtraItems.length" class="issue-meta-grid issue-meta-grid--extra">
          <div v-for="item in issueExtraItems" :key="item.key" class="issue-meta-item">
            <span class="issue-meta-label">{{ item.label }}</span>
            <span class="issue-meta-value" :title="item.value">{{ item.value }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Contenido -->
  <div class="flex px-4 pt-4 below-top-bar bg-grey-lightest flex-col lg:flex-row">
    <div class="w-full lg:hidden containt-tabs-menu">
      <b-tabs ref="refTabsMenu"
        type="is-toggle-rounded"
        v-model="tabSelectedMenu"
        expanded
        class="sticky top-0 mb-3"
        @input="_HandleChangeTab">

        <b-tab-item label="Comentarios"
          value="tab_menu_comments"
          icon="comments-alt"
          icon-pack="fas" class="hidden"></b-tab-item>

        <b-tab-item
          label="Modulos"
          value="tab_menu_modulos"
          icon="cogs"
          icon-pack="fas"></b-tab-item>

      </b-tabs>
    </div>

    <div class="w-full flex issue-columns" ref="issueColumns">
      <!-- Columna IZQUIERDA -->
      <div class="w-full lg:w-2/5 lg:mr-2" v-show="showPanel == 'left' || showPanel == 'both'">
        <div class="w-full column-scroll" :style="columnScrollStyle">
          <!-- Tarea (medición tipo task) desde la que se registró la incidencia -->
          <div v-if="linkedTask" class="pr-4 mb-4">
            <div class="flex overflow-hidden bg-white border border-gray-200 rounded-lg">
              <div class="w-1 flex-shrink-0" :class="linkedTask.isLocking ? 'bg-red' : 'bg-orange'" />
              <div class="flex-1 min-w-0 px-4 py-3">
                <div class="flex items-center gap-1.5 mb-1.5 text-blue-light">
                  <b-icon pack="fas" icon="tasks" size="is-small" />
                  <span class="text-xs font-bold tracking-wider uppercase">{{ $t('linked_task') }}</span>
                </div>
                <div class="flex items-baseline gap-2 text-base font-bold leading-snug break-words text-blue-darker" :title="linkedTask.name">
                  <span v-if="linkedTask.code" class="flex-shrink-0 text-xs font-semibold text-gray-500">{{ linkedTask.code }}</span>
                  <span>{{ linkedTask.name }}</span>
                </div>
                <div v-if="linkedTask.lockLabelKey" class="flex flex-wrap items-center gap-2 mt-2">
                  <span class="text-xs font-bold tracking-wide uppercase text-gray-400">{{ $t('lock_type') }}</span>
                  <span
                    class="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-semibold rounded-full"
                    :class="linkedTask.isLocking ? 'bg-red-lightest text-red-dark' : 'bg-orange-lightest text-orange-darker'"
                  >
                    <b-icon pack="fas" :icon="linkedTask.isLocking ? 'lock' : 'info-circle'" size="is-small" />
                    {{ $t(linkedTask.lockLabelKey) }}
                  </span>
                </div>
                <p v-if="linkedTask.observation" class="mt-2 text-sm text-gray-600 whitespace-pre-line">{{ linkedTask.observation }}</p>
              </div>
            </div>
          </div>
          <entity-description
            class="pr-4" v-if="issue.description"
            icon="align-left"
            :title="$t('issue_description')" :content="issue.description"
          />
          <entity-description
            class="pr-4" v-if="issue.steps_to_implement"
            icon="list-ol"
            :title="$t('issue_steps_to_implement')" :content="issue.steps_to_implement"
          />
          <entity-description
            class="pr-4" v-if="issue.steps_implemented"
            icon="check-double"
            :title="$t('issue_steps_implemented')" :content="issue.steps_implemented"
          />
          <issue-home-users
            :issue="issue"
            :project="project"
          />
          <entity-comments
            v-if="issue.id !== 0"
            :entity-id="issue.id"
            entity-type="issue"
            :can-add="issue.is_creator || issue.is_manager || issue.is_participant || issue.is_collaborator"
            :can-delete="canDeleteIssueContent"
            class="pr-4"
          />
        </div>
      </div>

      <!-- DERECHA -->
      <div class="w-full lg:w-3/5 lg:ml-2" v-show="showPanel == 'right' || showPanel == 'both'">
        <div class="w-full column-scroll" :style="columnScrollStyle">

          <div class="panel" v-if="issue.drawing">
            <div class="text-lg font-thin uppercase text-blue-light">Localización en plano</div>
            <canvas id="canvas" width="720" height="500" style="background-color: #f5f5f5"></canvas>
          </div>

          <div v-if="issue.pictogram_id" class="panel">

          </div>



          <div class="panel">
            <div class="text-lg font-thin uppercase text-blue-light">{{ $t('geolocation') }}</div>
            <b v-if="issue.location" class="text-gray text-sm rounded-xl inline-block mb-2 py-0.5 px-2 border-2 border-gray-400 bg-gray-200">{{ issue.location.name }}</b>
            <input type="hidden" name="address_id" id="address_id" value="">
            <googlemaps ref="mapRef"
              name="issue-location"
              :datamaps="datamaps"
              :draggable="draggable"
              :newPlace="newPlace"
              inputAutocomplete="address_id"/>
          </div>

          <!-- Documentos -->
          <div v-if="! isLoading">
            <entity-documents
              :entity-id="issue.id"
              entity-type="issue"
              :can-add="issue.is_creator || issue.is_manager || issue.is_participant || issue.is_collaborator"
              :can-delete="canDeleteIssueContent"
              :force-codification="project.codification_form === 'rejas'"
              class="my-4"
            />

            <!-- <entity-chat
                v-if="issue.id !== 0"
                :entity-id="entityId"
                entity-type="issue"
                :key="'issue.' + issue.id"
                :auto-open="withChat"
            /> chat desactivado -->
          </div>
          <!-- flujos de validacion -->
          <div v-if="entityId">
            <entity-validationflows
              ref="entity_validationflows"
              :entity-id="entityId"
              entity-type="issue"
            />
          </div>
          <div v-if="entityId && project.issue_template">
            <entity-issue-task
              ref="entity_issue_task"
              :entity-id="entityId"
              entity-type="issue"
              :data="issue"
              :is-open="issue.is_open"
            />
          </div>
        </div>
      </div>
    </div>
  </div>


  <div class="overflow-y-scroll slider bg-grey-darkest" :class="sliderClass" style="z-index: 70;">
    <!-- Info actividad -->
    <div class="info-entity">
        <div class="row-hide">
          <v-button-icon-round
            icon="arrow-right"
            color="blue"
            tooltip-position="left"
            class="float-right ml-6"
            @click="showSide"/>
        </div>
        <div class="text-xs text-grey">{{$t('issue_label')}}</div>
        <div class="text-sm font-semibold text-white">{{issue.name}}</div>
        <div class="float-left main-info">
          <div class="text-xs text-grey">{{$t('manager')}}</div>
          <div class="flex items-center info-data">
            <div class="w-8 h-8 mr-2 overflow-hidden border rounded-full border-grey" v-if="issue.manager">
                <img :src="issue.manager.avatar" :alt="issue.manager.fullname">
            </div>
            <div>
              <div class="text-sm text-blue-lighter" v-if="issue.manager && issue.manager.fullname">
                  {{ issue.manager.fullname }}
              </div>
              <div class="text-xs text-white" v-if="issue.manager && issue.manager.department">
                {{ issue.manager.department.name }}
              </div>
            </div>
          </div>
        </div>
        <div class="float-left main-info">
            <div class="text-xs text-grey">{{$t('created_at')}}</div>
            <div class="text-xs text-white info-data">{{issue.created_at}}</div>
            <div class="text-xs text-grey">{{$t('last_update')}}</div>
            <div class="text-xs text-white info-data">{{issue.updated_at}}</div>
        </div>
        <div class="more-info">
           <div class="text-xs text-grey">{{$t('issue_description')}}</div>
           <div class="text-xs text-white bg-white p-2 rounded-sm info-data" v-html="issue.description ? issue.description : 'Sin descripción'"></div>
        </div>
        <div class="more-info">
           <div class="text-xs text-grey">{{$t('users_participants')}}</div>
           <ul id="log-participants">
            <li v-for="user in issue.participants" :key="user.user_id" >
              <div class="flex items-center info-data" v-if="user['avatar'] && user['fullname']">
                <div class="w-8 h-8 mr-2 overflow-hidden border rounded-full border-grey">
                    <img :src="user['avatar']" :alt="user['fullname']">
                </div>
                <div>
                    <div class="text-sm text-blue-lighter">
                    {{ user['fullname'] }}
                    </div>
                    <div class="text-xs text-grey" v-if="user['department']">
                      {{ user['department']['name'] }}
                    </div>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <div class="more-info">
           <div class="text-xs text-grey">{{$t('log')}}</div>
           <ul id="log-actions">
            <li v-for="action of logActions" class="p-2">

              <div class="float-left w-full">

                  <div class="float-left w-8 h-8 overflow-hidden rounded-full">
                    <img :src="action.author.avatar" :alt="action.author.fullname">
                  </div>

                  <div class="float-left px-2 py-1 ml-3 text-xs text-white uppercase rounded-full" :class="action.entity_type_color">{{ action.entity_type }}</div>
                  <div class="float-left px-2 py-1 ml-3 text-xs text-white uppercase rounded-full" :class="action.action_type_color">{{ action.action_type }}</div>

              </div>

              <div class="float-left w-full mb-4 text-grey">
                <div class="text-xs text-left" style="margin-left:50px;">
                    {{ action.created_at }}
                </div>
              </div>

            </li>
          </ul>
        </div>

    </div>
  </div>
  <b-loading :is-full-page="false" :active.sync="isLoading" :can-cancel="false"></b-loading>

  <assignment-register-task-modal
    ref="assignment_register_task_modal"
    entity-type="activity"
    :subactivity-id="parseInt(subactivityId)"
    :activity-name="issue?.activity?.name"
    :entity-id="entityId"
    @reload="() => loadParams()"
  />
</div>
</template>

<script>
import Vue from "vue"
import { mapGetters } from 'vuex'
import EventBus from '~/plugins/bus'
import axios from 'axios'
import at from "~/store/modules/at";
import entityDescription from '·/components/entity-description'
import entityComments from '·/components/entity-comments'
import entityDocuments from '·/components/entity-documents'
import moment from 'moment'
import Googlemaps from '~/components/GoogleMaps.vue'
import PdfCanvas from "~/plugins/pdfCanvas";
import entityValidationflows from '·/components/entity-validationflows'
import entityIssueTask from '·/components/entity-issue-task'
import AssignmentRegisterTaskModal from '·/components/assignment-register-task-modal'
import PageHeader from '·/components/page-header'
import IssueHomeHeaderActions from '·/components/issue/issue-home-header-actions'
import IssueHomeUsers from '·/components/issue/issue-home-users'

const ASSIGNMENT_LINE_CLASS = 'App\\Models\\AssignmentLine'
const ISSUE_WITHOUT_LOCK = 22

// Mismo mapa que edit-issue (AssignmentLineStatus 22-28)
const TASK_LOCK_TYPE_KEYS = {
  22: 'task_issue_without_blocking',
  23: 'task_issue_with_task_blocking',
  24: 'task_issue_with_activity_blocking',
  28: 'task_issue_with_parent_activity_blocking',
  25: 'task_issue_with_project_blocking',
}

export default {
  middleware: 'auth',
  title: '',
  subtitle: 'issue_home_page_subtitle',
  menuOption: '2-8',
  hidePageTitle: true,

  components: {
    PageHeader,
    IssueHomeHeaderActions,
    IssueHomeUsers,
    entityDescription,
    entityValidationflows,
    entityIssueTask,
    entityComments,
    entityDocuments,
    Googlemaps,
    AssignmentRegisterTaskModal
  },

  data: () => ({
    issue: {
      id: 0,
      name: ''
    },
    atValues: [],
    sliderShown: false,
    sliderClass: '',
    activityId: undefined,
    subactivityId: undefined,
    logActions: undefined,
    isLoading: true,
    isActivityManager: false,
    // issueChatComponent: null,
    // issueDocumentsComponent: null,
    statusColor: 'blue',
    //mapa
    datamaps: [],
    draggable: false,
    newPlace: '',

    showPanelRight: true,
    showPanelLeft: true,

    tabSelectedMenu: -1, // tab seleccionado por defecto
    isOpenStatus: false,

    issueTasks: [],
    columnScrollHeight: null,
    showIssueDetails: false,
  }),

  watch: {
    '$route.params.issue_id': function() {
      // Reload params
      this.loadParams()
    },
    isLoading(value) {
      if (!value) {
        this.updateColumnScrollHeight()
      }
    },
    showPanel() {
      this.updateColumnScrollHeight()
    },
  },

  computed: {
    withChat() {
      return this.$route.params.with_chat
    },
    entityId: {
      get: function() {
        return parseInt(this.$route.params.issue_id)
      },
      set: function(newVal) {
        this.entityId = newVal
      }
    },
    ...mapGetters({
      company: 'app/company',
      project: 'app/project',
      mobile: 'device/mobile'
    }),

    // Project collaborators can view but not delete content (comments, documents).
    canDeleteIssueContent() {
      return !(this.project && this.project.can_collaborate)
    },

    columnScrollStyle() {
      if (!this.columnScrollHeight) {
        return {}
      }

      return { height: `${this.columnScrollHeight}px` }
    },

    issueSummaryFields() {
      return [
        {
          key: 'class',
          labelKey: 'classification',
          icon: 'shield-alt',
          value: this.issue && this.issue.class && this.issue.class.name ? this.issue.class.name : '-',
        },
        {
          key: 'type',
          labelKey: 'type',
          icon: 'cube',
          value: this.issue && this.issue.type && this.issue.type.name ? this.issue.type.name : '-',
        },
        {
          key: 'discipline',
          labelKey: 'discipline',
          icon: 'tag',
          value: this.issue && this.issue.discipline && this.issue.discipline.name ? this.issue.discipline.name : '-',
        },
        {
          key: 'origin',
          labelKey: 'origin',
          icon: 'flag',
          value: this.issue && this.issue.origin && this.issue.origin.name ? this.issue.origin.name : '-',
        },
        {
          key: 'location',
          labelKey: 'location',
          icon: 'map-marker-alt',
          value: this.issue && this.issue.location && this.issue.location.name ? this.issue.location.name : '-',
        },
      ]
    },

    linkedTask() {
      const line = this.issue && this.issue.assignable_type === ASSIGNMENT_LINE_CLASS ? this.issue.assignable : null
      const assignment = line && line.assignment
      if (!assignment) {
        return null
      }

      const lockStatus = [line.status, line.previous_status, assignment.issue_status]
        .map(Number)
        .find((status) => TASK_LOCK_TYPE_KEYS[status])

      return {
        code: assignment.code,
        name: assignment.name,
        observation: line.observation,
        lockLabelKey: lockStatus ? TASK_LOCK_TYPE_KEYS[lockStatus] : null,
        isLocking: !!lockStatus && lockStatus !== ISSUE_WITHOUT_LOCK,
      }
    },

    issueExtraItems() {
      const items = []

      if (this.issue && this.issue.supply && this.issue.supply.name) {
        items.push({ key: 'supply', label: this.$t('supply'), value: this.issue.supply.name })
      }
      if (this.issue && this.issue.provider && this.issue.provider.name) {
        items.push({ key: 'provider', label: this.$t('provider'), value: this.issue.provider.name })
      }

      if (this.issue && Array.isArray(this.issue.customfields)) {
        this.issue.customfields.forEach((cf, index) => {
          if (!cf || !cf.field) {
            return
          }

          items.push({
            key: `cf-${index}`,
            label: cf.field.name ? cf.field.name : '-',
            value: cf.value_free
              ? cf.value_free
              : (cf.value && cf.value.name ? cf.value.name : '-'),
          })
        })
      }

      return items
    },

    issueEstimatedResolutionLabel() {
      if (!this.issue || !this.issue.estimated_resolution_date) {
        return '-'
      }

      return moment(this.issue.estimated_resolution_date).format('DD-MM-YYYY')
    },

    issueDateLabel() {
      if (!this.issue || !this.issue.created_at) {
        return '-'
      }

      const formats = ['DD/MM/YYYY H:mm:ss', 'DD/MM/YYYY HH:mm:ss']
      const parsed = moment(this.issue.created_at, formats, true)
      const value = parsed.isValid() ? parsed : moment(this.issue.created_at)

      return value.isValid() ? value.format('DD-MM-YYYY') : this.issue.created_at
    },

    issueClosedDateLabel() {
      if (!this.issue || !this.issue.closed_at) {
        return '-'
      }

      const value = moment(this.issue.closed_at)

      return value.isValid() ? value.format('DD-MM-YYYY') : this.issue.closed_at
    },

    showPanel() {
      if( this.mobile.on_mobile ) {

        if( this.showPanelLeft && this.showPanelRight ) {
          this.tabSelectedMenu = 1
          this.showPanelLeft = false
        }

        return this.showPanelRight ? 'right' : 'left'
      }

      this.showPanelRight = true
      this.showPanelLeft = true
      return 'both'
    }
  },

  created() {
    this.loadParams();
    this.getIssueTasks()
    EventBus.$on('update:entityId', function(entityId) {
        this.entityId = entityId
    })
    EventBus.$on('issue-home:show-side', this.showSide)
    EventBus.$on('issue-home:register-task', this.onHeaderRegisterTask)
    EventBus.$on('issue-home:status-changed', this.onHeaderStatusChanged)
  },

  mounted() {
    this.updateColumnScrollHeight()
    window.addEventListener('resize', this.updateColumnScrollHeight)
  },

  beforeDestroy(){
    EventBus.$off("update:entityId");
    EventBus.$off('issue-home:show-side', this.showSide)
    EventBus.$off('issue-home:register-task', this.onHeaderRegisterTask)
    EventBus.$off('issue-home:status-changed', this.onHeaderStatusChanged)
    window.removeEventListener('resize', this.updateColumnScrollHeight)
  },

  methods: {
    loadParams() {
      this.activityId = this.$route.params.activity_id
      this.subactivityId = this.$route.params.subactivity_id ? this.$route.params.subactivity_id : this.subactivityId
      this.loadIssueData(this.$route.params.issue_id)
      this.loadIssueUsers(this.$route.params.issue_id)
    },

    updateDocumentTitle() {
      if (!this.issue || !this.issue.id) {
        return
      }

      this.$store.dispatch('app/updatePageInfo', {
        info: {
          title: this.issue.name,
          subtitle: this.$t('issue_home_page_subtitle'),
          menuOption: this.$options.menuOption,
          hidePageTitle: true,
        },
      })
    },

    onHeaderRegisterTask({ assignment, isReal, actionType }) {
      this.onRegisterTask(assignment, isReal, actionType)
    },

    onHeaderStatusChanged({ isOpen, statusColor }) {
      this.statusColor = statusColor
      this.isOpenStatus = isOpen
    },

    onRegisterTask(assignment, isReal=false, actionType = 1) {
      this.$refs.assignment_register_task_modal.show(assignment, isReal, actionType);
    },

    async getIssueTasks() {
      const { data } = await axios.get('/api/v2/issue/' + this.entityId + '/issue-tasks')
      if (data && data.success) {
        this.issueTasks = data.issueTasks
      } else {
        this.$notify.error('error_loading_issue_tasks')
      }
    },

    async loadIssueUsers(issueId){

      // Referenciamos
      var self = this;

      // Nos traemos usuarios de la incidencia para menciones
      const { data } = await axios.get(
        "/api/v2/issue/" + issueId + "/chat/users"
      );

      // Si users los mete en la store
      if (data && data.success) {

        data.data.forEach(function(element) {
          self.atValues.push(element)
        });

      }
      at.commit('increment', self.atValues)

    },

    loadIssueData(issueId) {

      var self = this

      axios.get('/api/v2/issue/' + issueId + '?h=1&with_doc=1')
        .then(function(response) {
          if (response.data && response.data.success) {

            let data = response.data
            // console.log('response: ', data)

            // Cargamos los datos de la incidencia
            self.issue = data.data
            self.isActivityManager = data.data.is_activity_manager;

            // Seteamos el proyecto y las tags
            let project = self.issue.project
            self.$store.dispatch('app/setProject', { project })
            let tags = project.tags
            self.$store.dispatch("app/setTags", { tags });

            // Cargamos los componentes que son dependientes de la información de esta llamada
            // self.issueChatComponent = "entity-chat"
            // self.issueDocumentsComponent = "entity-documents"


            self.issue.created_at = moment(self.issue.created_at).format('DD/MM/YYYY H:mm:ss')
            self.issue.updated_at = moment(self.issue.updated_at).format('DD/MM/YYYY H:mm:ss')

            // Asignamos la id de la actividad padre
            self.activityId = data.data.activity_id;

            self.statusColor = self.issue.is_open ? 'blue' : 'grey'
            self.isOpenStatus = self.issue.is_open ? true : false;

            // Actualizamos el título del documento (pestaña del navegador)
            self.updateDocumentTitle()

            // drawing
            if (self.issue.drawing) {
              self.loadDrawing(self.issue.drawing)
            }

            //map
            self.datamaps.push(self.issue.location)
            // al momento de emitir este evento, el componente GoogleMaps no está montado y el mapa se queda en gris
            // EventBus.$emit('load-locations', self.datamaps);
            // Parcheado con settimeout para que funcione. Arreglar.... #TODO
            self.$nextTick(() => {
              setTimeout(() => {
                EventBus.$emit('load-locations', self.datamaps);
              }, 2000);
            });

            self.isLoading = false

          } else if(response.data && response.data.deny){
            self.$notify.error('no_access_permissions')
            self.$router.push({ name: 'project.home', params: {} })
          } else {
            self.$notify.error('error_loading_issue_data')
            self.$router.push({ name: 'project.home', params: {} })
          }
        })
        .catch(error => {
          console.log(error.response);
          self.$notify.error('error_loading_issue_data')
          self.$router.push({ name: 'project.home', params: {} })
        });
    },

    async showSide() {
      this.sliderClass = this.sliderShown ? 'slide-out' : 'slide-in'
      this.sliderShown = !this.sliderShown
      //Obtenemos el log de la actividad
      if(!this.logActions){
        const { data } = await axios.get('/api/v2/issue/' + this.issue.id + '/log?h=1')
        if (data && data.success) {
          this.logActions = data.data
        }

      }
    },
    openChat(){
      EventBus.$emit('open-chat', 'issue.' + this.issue.id.toString())
    },

    // Muestra en el canvas la primera pagina del pdf del plano para mostrar ubicacion de incidencia
    async loadDrawing(drawing) {
      // Convertimos la pagina del plano a un png
      let imgSrc = await PdfCanvas.pdfToPng(drawing, {scale: 0.6})

      let canvas = document.getElementById('canvas')
      let context = canvas.getContext('2d');

      // La imagen png generada a partir del pdf
      let img = new Image();
      img.src = imgSrc

      // Ahora la dibujamos dentro del canvas
      img.onload = () => {

        context.drawImage(img, 0, 0);

        // una vez cargada la imagen "de fondo", pintamos encima cada marker
        this.issue.metadata?.markers?.forEach(marker => {

          const imgMarker = new Image();
          imgMarker.src = this.asset(PdfCanvas.markerImg.src)

          imgMarker.onload = () => {
            context.drawImage(imgMarker,
              ((img.width * marker.document_x) / 100) - PdfCanvas.markerImg.width , // coordenada x - anchura imagen
              ((img.height * marker.document_y) / 100) - PdfCanvas.markerImg.height // coordenada y - altura imagen
            );
          };
        })
      }
    },


    // Obsoleta, de cuando usabamos solo un marker. Se mostraba solo la porcion de documento alrededor del marker que cabia dentro del div
    async loadDrawingOneMarkerOLD(drawing) {

      // Ubicacion de la incidencia dentro del plano
      let location = {
          id: this.issue.id,
          title: this.issue.code + ' ' + this.issue.name,
          x: this.issue.metadata.document_x,
          y: this.issue.metadata.document_y
        }

      // Convertimos la pagina del plano a un png
      let imgSrc = await PdfCanvas.pdfToPng(drawing, {})

      let canvas = document.getElementById('canvas')
      let context = canvas.getContext('2d');

      // Cuánto hay que desplazar la imagen del plano para que salga centrado en el canvas
      let offsetX = 0
      let offsetY = 0
      // el centro del canvas (si incidencia menor que estos valores, no centrar imagen para que no se vea fondo blanco)
      let minX = canvas.width / 2;
      let minY = canvas.height / 2;

      // Será la imagen del marker de la ubicacion
      const imgMarker = new Image();

      // La imagen png generada a partir del pdf
      let img = new Image();
      img.src = imgSrc

      // Ahora la centramos dentro del canvas
      img.onload = () => {
        // window.open(img.src)
        // posicion de la issue en pixeles segun dimensiones imagen generada a partir de pdf
        offsetX = (img.width * location.x) / 100
        offsetY = (img.height * location.y) / 100

        offsetX = Math.max(offsetX - minX, 0);
        offsetY = Math.max(offsetY - minY, 0);
        // como el centrado será hacia la izquierda y/o hacia arriba, el desplazamiento será negativo
        context.drawImage(img, -1 * offsetX, -1 * offsetY);

        // una vez cargada la imagen "de fondo", pintamos encima el marker
        imgMarker.src = this.asset(PdfCanvas.markerImg.src)
      }



      // Y finalmente dibujamos un marker en el punto de la incidencia
      imgMarker.onload = () => {
        let markerOffsetX = 0
        let markerOffsetY = 0
        // Si el offset es 0, no hay desplazamiento de imagen (el marker está entre 0 y la mitad). Por tanto pintar el marker desplazado del centro
        if (offsetX == 0) {
          markerOffsetX -= (img.width * location.x) / 100 - minX // cuanto desplazado está de la mitad
        }
        if (offsetY == 0) {
          markerOffsetY -= (img.height * location.y) / 100 - minY
        }
        context.drawImage(
          imgMarker,
          (canvas.width / 2) - markerOffsetX - PdfCanvas.markerImg.width, // desde la mitad, resta el desplazamiento y resta la imagen
          (canvas.height / 2) - markerOffsetY - PdfCanvas.markerImg.height
        );
      };


    },

    _HandleChangeTab(value) {
      this.showPanelRight = value === 1
      this.showPanelLeft = value === 0
      this.updateColumnScrollHeight()
    },

    toggleIssueDetails() {
      this.showIssueDetails = !this.showIssueDetails
      this.$nextTick(() => {
        this.updateColumnScrollHeight()
      })
    },

    /**
     * Ajusta la altura scrolleable al espacio real bajo la cabecera,
     * para que el final del contenido no quede fuera del viewport.
     */
    updateColumnScrollHeight() {
      this.$nextTick(() => {
        const el = this.$refs.issueColumns
        if (!el) {
          return
        }

        const top = el.getBoundingClientRect().top
        this.columnScrollHeight = Math.max(180, Math.floor(window.innerHeight - top))
      })
    },

  },

};
</script>

<style scoped>
.issue-home-header-right {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem 1rem;
  width: 100%;
  min-width: 0;
}

.issue-home-header-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1rem;
  min-width: 0;
  flex: 1 1 12rem;
}

.issue-home-header-right ::v-deep .issue-home-header-actions {
  flex: 0 0 auto;
  margin-left: auto;
  max-width: 100%;
}

.issue-home-header-meta__status {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0.55rem;
  border: 1px solid;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}

.issue-home-header-meta__status.is-open {
  color: #9b2c2c;
  background: #fff5f5;
  border-color: #feb2b2;
}

.issue-home-header-meta__status.is-closed {
  color: #276749;
  background: #f0fff4;
  border-color: #9ae6b4;
}

.issue-home-header-meta__status-dot {
  width: 0.4rem;
  height: 0.4rem;
  border-radius: 999px;
  background: currentColor;
}

.issue-home-header-meta__divider {
  width: 1px;
  height: 1.75rem;
  background: #e2e8f0;
  flex-shrink: 0;
}

.issue-home-header-meta__item {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}

.issue-home-header-meta__label {
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #7d99d4;
  white-space: nowrap;
}

.issue-home-header-meta__value {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8125rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
}

.issue-home-header-meta__value--date {
  color: #b7791f;
}

@media (max-width: 1023px) {
  .issue-home-header-meta__divider {
    display: none;
  }
}

.containt-tabs-menu::v-deep .b-tabs section {
  display: none;
}
.issue-summary {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.875rem 1rem;
}
.issue-summary-bar {
  display: flex;
  align-items: stretch;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}
.issue-summary-field {
  flex: 1 1 0;
  min-width: 0;
  padding: 0.75rem 1rem;
  border-right: 1px solid #e2e8f0;
}
.issue-summary-field:last-child {
  border-right: 0;
}
.issue-summary-field__label {
  display: block;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #a0aec0;
  margin-bottom: 0.35rem;
}
.issue-summary-field__value {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  max-width: 100%;
  font-size: 0.875rem;
  font-weight: 700;
  color: #2c5282;
  line-height: 1.2;
}
.issue-summary-field__value span:last-child {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.issue-status-badge {
  display: inline-flex;
  align-items: center;
  font-family: Raleway, sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #fff;
  border-radius: 999px;
  padding: 0.3rem 0.75rem;
}
.issue-status-badge.is-open {
  background: #e53e3e;
}
.issue-status-badge.is-closed {
  background: #38a169;
}
.issue-date-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #4a5568;
  background: #edf2f7;
  border-radius: 999px;
  padding: 0.25rem 0.7rem;
}
.issue-meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 0.65rem 1rem;
}
.issue-meta-grid--extra {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e2e8f0;
}
.issue-meta-item {
  min-width: 0;
}
.issue-meta-label {
  display: block;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #63b3ed;
  margin-bottom: 0.15rem;
}
.issue-meta-value {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #2d3748;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.issue-details-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.75rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: #3182ce;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
}
.issue-details-toggle:hover {
  color: #2b6cb0;
}
.issue-details-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.35rem;
  border-radius: 999px;
  background: #ebf8ff;
  color: #2b6cb0;
  font-size: 0.7rem;
}
.column-scroll {
  /* Fallback hasta que JS mida el espacio disponible bajo la cabecera */
  height: calc(100vh - 220px);
  max-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;
}
.column-scroll:not(:last-child) {
  margin-right: 0.5rem;
  border-color: #adc2ea;
  border-right-width: 1px;
}
/* .column-scroll:last-child {
  margin-left: 0.5rem;
} */
.slider {
  position: fixed;
  top: 61px;
  right: 0;
  bottom: 0;
  width: 360px;
  transform: translateX(100%);
  -webkit-transform: translateX(100%);
}
.slide-in {
  animation: slide-in 0.5s forwards;
  -webkit-animation: slide-in 0.5s forwards;
}
.slide-out {
  animation: slide-out 0.5s forwards;
  -webkit-animation: slide-out 0.5s forwards;
}
@keyframes slide-in {
  100% { transform: translateX(0%); }
}
@-webkit-keyframes slide-in {
  100% { -webkit-transform: translateX(0%); }
}
@keyframes slide-out {
  0% { transform: translateX(0%); }
  100% { transform: translateX(100%); }
}
@-webkit-keyframes slide-out {
  0% { -webkit-transform: translateX(0%); }
  100% { -webkit-transform: translateX(100%); }
}

.info-entity{padding: 20px;}
.info-entity .row-hide{float: right; width: 100%;}
.info-entity .main-info{padding-right: 12px; margin-top: 20px; width: 50%;}
.info-entity .main-info~.main-info{padding-right: 0;}
.info-entity .info-data{margin-top: 10px; margin-bottom: 10px;}
.info-entity .more-info{display: inline-block; width: 100%; margin-top: 20px;}

.timeline-box {
    position: relative;
    margin-top: 20px;
}
.timeline-box:before {
    position: absolute;
    top: 0;
    left: 50%;
    width: 0;
    height: 100%;
    margin-left: 0;
    content: '';
    border-left: 1px solid transparent;
    border-color: #ffffff;
}
.timeline-box .tl-row {
    position: relative;
    padding: 10px 0;
}
.timeline-box .tl-row .tl-item {
    width: 50%;
}
.timeline-box .popover {
    min-width: 50px;
}
.timeline-box .tl-row .tl-item .popover {
    position: relative;
    display: block;
    margin: 0;
    box-shadow: 0 0 0 transparent;
}
.timeline-box .tl-row .tl-item .popover.left {
    float: right;
    margin-right: 35px;
}
.timeline-box .tl-row .tl-item.float-right .popover.right {
    margin-left: 35px;
    background: white;
}
.timeline-box .tl-row .tl-item .tl-panel {
    font-weight: bold;
    position: absolute;
    top: 50%;
    left: 50%;
    width: 50%;
    margin-top: -8px;
    margin-left: 23px;
    text-align: left;
}
.timeline-box .tl-row .tl-item.float-right .tl-panel {
    right: 50%;
    left: auto;
    margin-right: 23px;
    margin-left: 0;
    text-align: right;
}
.timeline-box .tl-row .tl-item .tl-title {
    font-size: 15px;
    font-weight: bold;
    margin: 0 0 10px;
}
.timeline-box .tl-row .tl-item .tl-label {
    display: inline-block;
    margin-bottom: 10px;
    padding: 5px 8px;
}
.timeline-box .tl-row .tl-item .tl-content {
    font-size: 13px;
    margin: 0 0 10px;
    opacity: .80;
    -moz-opacity: .80;
    filter: alpha(opacity: 80);
}
.timeline-box .tl-row .tl-item .tl-time {
    font-style: italic;
    opacity: .40;
    -moz-opacity: .40;
    filter: alpha(opacity: 40);
}
.timeline-box .tl-row .tl-item .tl-icon,
.timeline-box .tl-row .tl-item .tl-bullet {
    position: absolute;
    top: 50%;
    left: 50%;
    display: block;
    width: 17px;
    height: 17px;
    margin-top: -9px;
    margin-left: -8px;
    border-radius: 100px;
}
.timeline-box .tl-row .tl-item .tl-bullet:before {
    display: block;
    width: 9px;
    height: 9px;
    margin: 4px 0 0 4px;
    content: '';
    border-radius: 100px;
    background: #fff;
}
.timeline-box .tl-row .tl-item .tl-icon {
    font-size: 18px;
    line-height: 34px;
    width: 34px;
    height: 34px;
    margin-top: -18px;
    margin-left: -17px;
    text-align: center;
}
/* Timeline Horizontal */

.timeline-scroll {
    overflow-x: scroll;
    overflow-y: hidden;
}
.timeline-horizontal {
    height: 400px;
    padding: 10px 0;
}
.timeline-horizontal .tl-row {
    float: left;
    width: 400px;
    height: 173px;
}
.timeline-horizontal.timeline-box:before {
    top: 50%;
    left: 0;
    width: 100%;
    height: 1px;
    border: 0;
}
.timeline-horizontal.timeline-box .tl-row .tl-item {
    width: auto;
}
.timeline-horizontal.timeline-box .tl-row .tl-item .tl-panel {
    top: auto;
    bottom: -60px;
    left: 0;
    width: 100%;
    margin: 0;
    text-align: center;
}
.timeline-horizontal.timeline-box .tl-row .tl-item .tl-bullet {
    margin-left: -9px;
}
.timeline-horizontal.timeline-box .tl-row .tl-item .tl-icon,
.timeline-horizontal.timeline-box .tl-row .tl-item .tl-bullet {
    top: auto;
    bottom: -27px;
}
.timeline-horizontal.timeline-box .tl-row .tl-item .popover {
    position: absolute;
    top: auto;
    bottom: 10px;
    width: 100%;
}
.timeline-horizontal.timeline-box .tl-row .tl-item.float-right .tl-panel {
    bottom: 0;
}
.timeline-horizontal.timeline-box .tl-row .tl-item.float-right .popover {
    top: 220px;
    bottom: auto;
}

/* Timeline columns */

.timeline-box-right.timeline-box .tl-row .tl-item,
.timeline-box-left.timeline-box .tl-row .tl-item {
    width: auto;
    float: none !important;
}

.timeline-box-right.timeline-box .tl-row .tl-item .popover,
.timeline-box-left.timeline-box .tl-row .tl-item .popover {
    float: none;
}

.timeline-box-right.timeline-box:before {
    left: auto;
    right: 17px;
}

.timeline-box-right.timeline-box .tl-row {
    padding-right: 20px;
}

.timeline-box-right.timeline-box .tl-row .tl-item .tl-icon,
.timeline-box-right.timeline-box .tl-row .tl-item .tl-bullet {
    left: auto;
    right: 17px;
    margin-left: 0;
    margin-right: -17px;
}

.timeline-box-left.timeline-box:before {
    right: auto;
    left: 17px;
}

.timeline-box-left.timeline-box .tl-row {
    padding-left: 20px;
}

.timeline-box-left.timeline-box .tl-row .tl-item .tl-icon,
.timeline-box-left.timeline-box .tl-row .tl-item .tl-bullet {
    right: auto;
    left: 17px;
    margin-right: 0;
    margin-left: -17px;
}

.popover-content{padding: 12px;}

.popover.right > .arrow:after {
    bottom: -10px;
    left: 1px;
    content: " ";
    border-right-color: #fff;
    border-left-width: 0;
}
.popover.right .arrow:after {
    bottom: -10px;
    left: 1px;
    border-right-color: rgba(0, 0, 0, .2);
    border-left-width: 0;
}

body .popover.right .arrow:after {
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 10px 15px 10px 0;
    border-color: transparent #ffffff transparent transparent;
    left: -15px !important;
    position: absolute;
    top: 43%;
}

@media (min-width: 640px) {
    .sm\:block {
        display: block !important;
    }
}
</style>
